/**
 * 认证工具模块
 *
 * Token 通过 httpOnly cookie（access_token / refresh_token）传递，
 * 由后端在 login/refresh 时 Set-Cookie，前端无需手动管理，杜绝 XSS 窃取风险。
 *
 * 降级兼容：开发/特殊场景下可通过 localStorage 缓存 user_info 供 UI 快速展示，
 * 但认证本身完全依赖 cookie。
 */

export interface UserInfo {
  id: number;
  username: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  department_id?: number;
  department_name?: string;
  position?: string;
  hire_date?: string;
  is_superuser: number;
  status: number;
  feishu_auto_sync?: number;
  roles?: string[];
  permissions?: string[];
}

function safeStore(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* 隐私模式或配额满 */ }
}

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeRemove(key: string) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * 仅缓存 user_info 用于 UI 快速渲染（头像、姓名等）。
 * 认证 token 已由 httpOnly cookie 管理，不再存入 localStorage。
 */
export function setUserInfo(user: UserInfo) {
  safeStore('user_info', JSON.stringify(user));
}

export function getUserInfo(): UserInfo | null {
  const raw = safeGet('user_info');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

export function clearAuth() {
  safeRemove('user_info');
}

/**
 * 通过 cookie 认证，前端的 isAuthenticated 检查退化为：
 * 本地有缓存的 user_info 即认为已登录（token 有效性由后端校验）。
 * 若 401 触发重定向，会同步清理 user_info。
 */
export function isAuthenticated(): boolean {
  return getUserInfo() !== null;
}
